<template>
  <div
    class="content-banner"
    :class="[bannerClass, { 'content-banner-has-additional-content': $slots.additionalContent }]"
  >
    <div class="content-banner-top-row">
      <div
        class="content-banner-body"
        :class="{ 'content-banner-body-with-icon': icon }"
      >
        <div class="content-banner-title-row">
          <DpplSvg
            v-if="icon"
            :file-name="icon"
            :color="iconColor"
            class="content-banner-icon"
          />
          <span class="content-banner-title text-h2">{{ title }}</span>
        </div>
        <div class="content-banner-slot">
          <slot />
        </div>
      </div>
    </div>
    <div
      v-if="$slots.additionalContent"
      class="content-banner-additional-content"
    >
      <slot name="additionalContent" />
    </div>
    <div class="content-banner-actions">
      <slot name="actions">
        <v-btn
          color="brandPrimary"
          variant="flat"
          size="small"
          @click="$emit('primary-action')"
        >
          {{ primaryLabel }}
        </v-btn>
        <v-btn
          v-if="secondaryLabel"
          variant="outlined"
          size="small"
          :color="secondaryColor"
          :class="[
            'content-banner-secondary-btn',
            { 'content-banner-secondary-btn-error': secondaryColor === 'error' },
            { 'content-banner-secondary-btn-brand': secondaryColor === 'brandPrimary' },
          ]"
          @click="$emit('secondary-action')"
        >
          {{ secondaryLabel }}
        </v-btn>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import { useColors } from '@/composables/colors';

const {
  red700,
  yellow500,
  blue500,
  orange700,
} = useColors();

const props = defineProps({
  variant: {
    type: String,
    default: 'error',
    validator: (variant) => [
      'error', 'warning', 'info', 'incomplete',
    ].includes(variant),
  },
  icon: { type: String, default: '' },
  title: { type: String, required: true },
  primaryLabel: { type: String, required: true },
  secondaryLabel: { type: String, default: '' },
  secondaryColor: { type: String, default: '' },
});

defineEmits(['primary-action', 'secondary-action']);

const bannerClass = computed(() => `content-banner-${props.variant}`);

const iconColor = computed(() => {
  const variantColors = {
    error: red700.value,
    warning: yellow500.value,
    info: blue500.value,
    incomplete: orange700.value,
  };

  return variantColors[props.variant] || red700.value;
});
</script>

<style scoped lang="less">
.content-banner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid transparent;

  &.content-banner-has-additional-content {
    gap: 8px;
  }

  &.content-banner-error {
    background-color: @red-100;
    border-color: @red-300;

    .content-banner-title {
      color: @red-700;
    }
  }

  &.content-banner-warning {
    background-color: @yellow-100;
    border-color: @yellow-300;

    .content-banner-title {
      color: @yellow-700;
    }
  }

  &.content-banner-info {
    background-color: @navy-100;
    border-color: @navy-300;

    .content-banner-title {
      color: @navy-700;
    }
  }

  &.content-banner-incomplete {
    background-color: @orange-100;
    border-color: @orange-300;

    .content-banner-title {
      color: @orange-700;
    }
  }
}

.content-banner-top-row {
  display: flex;
  align-items: flex-start;
  min-width: 0;
}

.content-banner-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.content-banner-body-with-icon .content-banner-slot {
  margin-left: 30px;
}

.content-banner-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.content-banner-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.content-banner-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-shrink: 0;

  :deep(.content-banner-secondary-btn) {
    border-color: currentColor;
  }

  :deep(.content-banner-secondary-btn-error) {
    background-color: @bg-default;
    color: @text-error;
    border-color: @stroke-default;
  }

  :deep(.content-banner-secondary-btn-brand) {
    background-color: @bg-default;
    color: @brand-primary;
    border-color: @stroke-default;
  }
}

.content-banner-secondary-btn {
  border-color: currentColor;
}

.content-banner-secondary-btn-error {
  background-color: @bg-default;
  color: @text-error;
  border-color: @stroke-default;
}

.content-banner-secondary-btn-brand {
  background-color: @bg-default;
  color: @brand-primary;
  border-color: @stroke-default;
}

.content-banner-additional-content {
  width: 100%;
  min-width: 0;
}

.content-banner:has(.content-banner-body-with-icon) .content-banner-actions {
  padding-left: 30px;
}

.content-banner-has-additional-content:has(.content-banner-body-with-icon) .content-banner-additional-content {
  padding-left: 30px;
}
</style>
